/** Which drawings are open LOCALLY — the one place `api.ts` asks before it reaches the network.
 *
 * ⚠⚠ **THIS IS THE WHOLE SEAM, AND IT IS DELIBERATELY ONE LOOKUP.** `Studio.tsx` calls
 * `loadDisplay`, `editDrawing` and the rest from dozens of places; routing local mode by editing
 * those call sites would be dozens of chances to miss one, and the one missed would silently
 * talk to the server about a drawing the server does not have. So the decision lives inside
 * `api.ts`'s own functions: if this registry has the id, the answer comes from the browser.
 *
 * ⚠ The id is the SERVER's id where the drawing came from one, and a negative number where it
 * did not — a file opened from disk has no server id and must still be addressable by everything
 * that takes one.
 */
import type { LocalDrawing } from "./adapter";

const open = new Map<number, LocalDrawing>();
let nextLocalId = -1;

/** The drawing open locally under this id, or `undefined`. */
export function localFor(id: number | null | undefined): LocalDrawing | undefined {
  return id === null || id === undefined ? undefined : open.get(id);
}

/** Is anything open locally? ⚠ What the status line asks, so it can say so: a drafter must
 *  always know whether their work is on the server or in the tab. */
export function anyLocal(): boolean {
  return open.size > 0;
}

/** An id for a drawing that has no server id — a file opened from disk.
 *  ⚠ NEGATIVE on purpose: it can never collide with a real one, and a request that leaked to the
 *  server with it would 404 loudly instead of touching somebody else's drawing. */
export function localId(): number {
  return nextLocalId--;
}

export function registerLocal(id: number, d: LocalDrawing): void {
  open.set(id, d);
}

/** Forget it, and let the engine go. */
export async function forgetLocal(id: number): Promise<void> {
  const d = open.get(id);
  open.delete(id);
  await d?.close();
}

/** Forget EVERY local drawing, and close the store the engine is holding.
 *
 * ⚠⚠⚠ **THE MODULE HOLDS ONE DOCUMENT AND THE ABI HAS NO ROOM FOR A SECOND.** `pdcad-wasm`'s
 * `STORE` is a single `Option<Store>`, and `store.display`, `store.layouts`, `store.layers` —
 * every `store.*` call — take no id. So a second `dwg.open` REPLACES the document while this
 * registry still holds the first drawing's id, and every later call for that drawing addresses
 * the new one.
 *
 * ⚠⚠ **AND IT IS USUALLY SILENT.** The owner found it because the two drawings' layouts happened
 * to be named differently — opening a second file printed
 * *`no layout called "CGR-DD-ARC-ELC-053"`*, which at least SAYS something is wrong. Two files
 * that both call their sheet `Layout1` would have drawn the second drawing's geometry under the
 * first one's name with nothing to see.
 *
 * ⇒ One local drawing at a time, said out loud, until the store is keyed. Closing FIRST, before
 * the new `dwg.open`, because `close()` clears the module's store and doing it afterwards would
 * throw away the drawing just opened.
 */
export async function forgetAllLocal(): Promise<number[]> {
  const ids = [...open.keys()];
  // ⚠ ONE close, not one per id: they all name the same store, and the second would be closing
  // a document that is already gone.
  const last = [...open.values()].pop();
  open.clear();
  await last?.close();
  return ids;
}

/** Every local drawing, for a "you have unsaved work" prompt. */
export function localDrawings(): Array<[number, LocalDrawing]> {
  return [...open.entries()];
}
